import { borgAnalytics, Button, errorIcon, getChannel, locationReload, useTheme } from '@ytl/common-web';
import { useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/languageContext';
import { useOpenAm } from '../contexts/openAmContext';
import { AuthError, OpenAmAuthError } from '../shared/@types/OpenAmInitialData';

const isAuthError = (e: unknown): e is AuthError => !!e && typeof e === 'object' && 'type' in e && 'callback' in e;

export const ErrorView = () => {
  const { error } = useOpenAm();
  const { translation } = useLanguage();
  const { isWebView } = useTheme();

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'Login failed',
    });
  }, [isWebView]);

  const icon = useMemo(() => {
    return errorIcon.icon;
  }, []);

  const header = useMemo(() => {
    if (isAuthError(error)) {
      switch (error.type) {
        case OpenAmAuthError.MSISDNRetryLimit:
          return translation.loginRegister.login.loginError.retryLimitHeader;
        default:
          return translation.loginRegister.login.loginError.header;
      }
    }
    switch (error) {
      case OpenAmAuthError.OauthError:
        return translation.loginRegister.login.box.oauthError.header('');
      case OpenAmAuthError.InitError:
        return translation.loginRegister.login.failedToInit.header;
      case OpenAmAuthError.LoginError:
        return translation.loginRegister.login.loginError.header;
      case OpenAmAuthError.TimeoutError:
        return translation.loginRegister.login.loginError.header;
      case OpenAmAuthError.MSISDNResendLimit:
        return translation.loginRegister.login.loginError.resendLimitHeader;
      case OpenAmAuthError.MSISDNRetryLimit:
        return translation.loginRegister.login.loginError.retryLimitHeader;
      case OpenAmAuthError.Blocked:
        return translation.loginRegister.login.loginError.blockedHeader;
      default:
        return translation.loginRegister.login.loginError.header;
    }
  }, [translation, error]);

  const content = useMemo(() => {
    if (isAuthError(error)) {
      switch (error.type) {
        case OpenAmAuthError.MSISDNRetryLimit:
          return translation.loginRegister.login.loginError.retryLimitContent;
        default:
          return translation.loginRegister.login.loginError.content;
      }
    }
    switch (error) {
      case OpenAmAuthError.OauthError:
        return translation.loginRegister.login.box.oauthError.content('');
      case OpenAmAuthError.InitError:
        return translation.loginRegister.login.failedToInit.content;
      case OpenAmAuthError.LoginError:
        return translation.loginRegister.login.loginError.content;
      case OpenAmAuthError.TimeoutError:
        return translation.loginRegister.login.box.loginError.sessionTimeout;
      case OpenAmAuthError.MSISDNResendLimit:
        return translation.loginRegister.login.loginError.resendLimitContent;
      case OpenAmAuthError.MSISDNRetryLimit:
        return translation.loginRegister.login.loginError.retryLimitContent;
      case OpenAmAuthError.Blocked:
        return translation.loginRegister.login.loginError.blockedContent;
      default:
        return translation.loginRegister.login.loginError.content;
    }
  }, [translation, error]);

  const button = useMemo(() => {
    if (isAuthError(error)) {
      switch (error.type) {
        case OpenAmAuthError.MSISDNRetryLimit:
          return (
            <Button
              variant={'contained'}
              onClick={() => {
                error.callback();
              }}
            >
              {translation.loginRegister.login.msisdnverification.resendAction}
            </Button>
          );
        default:
          return (
            <Button
              variant={'contained'}
              onClick={() => locationReload()}
            >
              {translation.loginRegister.login.loginError.action}
            </Button>
          );
      }
    }
    switch (error) {
      case OpenAmAuthError.OauthError:
        return <Button variant={'contained'}>{translation.loginRegister.login.box.oauthError.action}</Button>;
      case OpenAmAuthError.InitError:
        return (
          <Button
            variant={'contained'}
            onClick={() => locationReload()}
          >
            {translation.loginRegister.login.failedToInit.action}
          </Button>
        );
      case OpenAmAuthError.LoginError:
        return (
          <Button
            variant={'contained'}
            onClick={() => locationReload()}
          >
            {translation.loginRegister.login.loginError.action}
          </Button>
        );
      case OpenAmAuthError.TimeoutError:
        return (
          <Button
            variant={'contained'}
            onClick={() => locationReload()}
          >
            {translation.loginRegister.login.loginError.action}
          </Button>
        );
      case OpenAmAuthError.Blocked:
        return (
          <Button
            variant={'contained'}
            onClick={() => locationReload()}
          >
            {translation.loginRegister.login.loginError.action}
          </Button>
        );
      default:
        return (
          <Button
            variant={'contained'}
            onClick={() => locationReload()}
          >
            {translation.loginRegister.login.loginError.action}
          </Button>
        );
    }
  }, [translation, error]);

  return (
    <>
      <span aria-hidden="true">{icon}</span>
      <h3 dangerouslySetInnerHTML={{ __html: header }} />
      <p>{content}</p>
      {button}
    </>
  );
};
